import { useQuery } from '@tanstack/react-query';
import { MessageSquare, Plus, Ticket as TicketIcon } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, type Paginated, type TicketGroup, type TicketRow } from '../lib/api';
import { useAuth } from '../lib/auth';
import { priorityLabel, statusLabel, useI18n, type TranslationKey } from '../lib/i18n';
import { formatDate } from '../lib/format';
import { companyName } from '../lib/people';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Input,
  LoadingBlock,
  PageHeader,
  Pagination,
  Table,
  Tabs,
  Td,
  Th,
  Tr,
  priorityTone,
  statusTone,
} from '../components/ui';

const GROUPS: Array<{ id: TicketGroup | 'all'; label: TranslationKey }> = [
  { id: 'all', label: 'common.all' },
  { id: 'open', label: 'tickets.groupOpen' },
  { id: 'inProgress', label: 'tickets.groupInProgress' },
  { id: 'finished', label: 'tickets.groupFinished' },
  { id: 'cancelled', label: 'tickets.groupCancelled' },
];

/** Every ticket for the client — the ones it raised and the ones we raised for it. */
export function TicketsPage() {
  const { t, locale, localized } = useI18n();
  const { account } = useAuth();
  const company = companyName(account, locale);
  const navigate = useNavigate();
  // The tab is in the address, so the dashboard's tiles open the right one.
  const [params, setParams] = useSearchParams();
  const requested = params.get('group');
  const group = GROUPS.some((g) => g.id === requested) ? (requested as TicketGroup) : 'all';
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['tickets', group, page, q],
    queryFn: () =>
      api.get<Paginated<TicketRow>>('/tickets', {
        page,
        pageSize: 25,
        group: group === 'all' ? undefined : group,
        q: q.trim() || undefined,
      }),
  });

  const { data: board } = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => api.get<{ canRaiseTickets: boolean }>('/dashboard'),
  });

  return (
    <>
      <PageHeader
        title={t('tickets.title')}
        count={data?.total}
        action={
          board?.canRaiseTickets !== false && (
            <Button variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => navigate('/tickets/new')}>
              {t('tickets.new')}
            </Button>
          )
        }
      />

      <Tabs
        active={group}
        onChange={(id) => {
          setPage(1);
          setParams(id === 'all' ? {} : { group: id }, { replace: true });
        }}
        tabs={GROUPS.map((g) => ({ id: g.id, label: t(g.label) }))}
      />

      <Card className="mt-4" padded={false}>
        <div className="p-4">
          <Input
            placeholder={t('common.search')}
            aria-label={t('common.search')}
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
            className="max-w-sm"
          />
        </div>
        {isLoading ? (
          <LoadingBlock />
        ) : error ? (
          <ErrorState error={error} onRetry={() => void refetch()} />
        ) : !data?.items.length ? (
          <EmptyState
            icon={<TicketIcon className="h-8 w-8" />}
            title={group === 'all' && !q ? t('tickets.empty') : undefined}
            hint={group === 'all' && !q ? t('tickets.emptyHint') : undefined}
          />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>{t('tickets.number')}</Th>
                  <Th>{t('tickets.subject')}</Th>
                  <Th>{t('common.status')}</Th>
                  <Th>{t('common.priority')}</Th>
                  <Th align="end">{t('tickets.replies')}</Th>
                  <Th align="end">{t('common.created')}</Th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((ticket) => (
                  <Tr key={ticket.id} onClick={() => navigate(`/tickets/${ticket.id}`)}>
                    <Td>
                      <span className="font-mono text-mono-sm text-primary-600 dark:text-primary-400">{ticket.number}</span>
                    </Td>
                    <Td className="max-w-md">
                      <span className="block truncate">{ticket.subject}</span>
                      <span className="block truncate text-caption text-faint">
                        {ticket.project ? localized(ticket.project) : t('common.noProject')} ·{' '}
                        {ticket.raisedByClient
                          ? // Several people sign in for the client: "you" only for the reader's own.
                            !ticket.raisedBy || ticket.raisedBy.you
                            ? t('tickets.raisedByYou')
                            : t('tickets.raisedByName', { name: ticket.raisedBy.name ?? company })
                          : ticket.requestedBy
                            ? t('tickets.raisedByUsFor', { name: ticket.requestedBy.name })
                            : t('tickets.raisedByUs')}
                      </span>
                    </Td>
                    <Td>
                      <Badge tone={statusTone(ticket.status)}>{statusLabel(t, 'ticket', ticket.status)}</Badge>
                    </Td>
                    <Td>
                      <Badge tone={priorityTone(ticket.priority)}>{priorityLabel(t, ticket.priority)}</Badge>
                    </Td>
                    <Td align="end" className="text-muted">
                      <span className="inline-flex items-center gap-1 tabular">
                        <MessageSquare className="h-3.5 w-3.5" aria-hidden />
                        {ticket.replies}
                      </span>
                    </Td>
                    <Td align="end" className="whitespace-nowrap text-caption text-muted">
                      {formatDate(ticket.createdAt, locale)}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
            <Pagination page={data.page} pageCount={data.pageCount} total={data.total} onChange={setPage} />
          </>
        )}
      </Card>
    </>
  );
}
